import { useState, useRef, useEffect } from 'react';

const findScrollableElement = (el: HTMLElement): HTMLElement | null => {
  if (!el) return null;

  const hasHorizontalScroll = el.scrollWidth > el.clientWidth;
  const hasVerticalScroll = el.scrollHeight > el.clientHeight;
  const style = globalThis.getComputedStyle(el);
  const isScrollable = style.overflow !== 'visible' && style.overflow !== 'hidden';

  // Check children first to find the deepest scrollable element
  for (const child of Array.from(el.children) as HTMLElement[]) {
    const scrollable = findScrollableElement(child);
    if (scrollable) return scrollable;
  }

  // Then check current element
  if ((hasHorizontalScroll || hasVerticalScroll) && isScrollable) {
    return el;
  }

  return null;
};

export const useDrag = (
  viewerRef: React.RefObject<HTMLDivElement>,
  scale: number,
  defaultZoomScale: number,
  fileType: string | null
) => {
  const [isDragging, setIsDragging] = useState(false);
  const scrollableElementRef = useRef<HTMLElement | null>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const scrollStartRef = useRef({ left: 0, top: 0 });

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (scale > defaultZoomScale && viewerRef.current) {
      e.preventDefault();
      e.stopPropagation();

      const scrollableEl = findScrollableElement(viewerRef.current) || viewerRef.current;
      scrollableElementRef.current = scrollableEl;

      setIsDragging(true);
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      scrollStartRef.current = {
        left: scrollableEl.scrollLeft,
        top: scrollableEl.scrollTop
      };
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (scale > defaultZoomScale && viewerRef.current) {
      e.preventDefault();
      e.stopPropagation();

      const scrollableEl = findScrollableElement(viewerRef.current) || viewerRef.current;
      scrollableElementRef.current = scrollableEl;

      setIsDragging(true);
      const touch = e.touches[0];
      dragStartRef.current = { x: touch.clientX, y: touch.clientY };
      scrollStartRef.current = {
        left: scrollableEl.scrollLeft,
        top: scrollableEl.scrollTop
      };
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (scale <= defaultZoomScale || !viewerRef.current) return;

    const scrollAmount = 50; // pixels to scroll per key press
    const scrollableEl = scrollableElementRef.current || viewerRef.current;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        scrollableEl.scrollTop = Math.max(0, scrollableEl.scrollTop - scrollAmount);
        break;
      case 'ArrowDown':
        e.preventDefault();
        scrollableEl.scrollTop += scrollAmount;
        break;
      case 'ArrowLeft':
        e.preventDefault();
        scrollableEl.scrollLeft = Math.max(0, scrollableEl.scrollLeft - scrollAmount);
        break;
      case 'ArrowRight':
        e.preventDefault();
        scrollableEl.scrollLeft += scrollAmount;
        break;
      case 'Home':
        e.preventDefault();
        scrollableEl.scrollTop = 0;
        scrollableEl.scrollLeft = 0;
        break;
      case 'End':
        e.preventDefault();
        scrollableEl.scrollTop = scrollableEl.scrollHeight;
        scrollableEl.scrollLeft = scrollableEl.scrollWidth;
        break;
    }
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  // Global mouse and touch event handlers
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging && scrollableElementRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;

        const newScrollLeft = scrollStartRef.current.left - deltaX;
        const newScrollTop = scrollStartRef.current.top - deltaY;

        requestAnimationFrame(() => {
          if (scrollableElementRef.current) {
            scrollableElementRef.current.scrollLeft = newScrollLeft;
            scrollableElementRef.current.scrollTop = newScrollTop;
          }
        });
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (isDragging && scrollableElementRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartRef.current.x;
        const deltaY = touch.clientY - dragStartRef.current.y;

        const newScrollLeft = scrollStartRef.current.left - deltaX;
        const newScrollTop = scrollStartRef.current.top - deltaY;

        requestAnimationFrame(() => {
          if (scrollableElementRef.current) {
            scrollableElementRef.current.scrollLeft = newScrollLeft;
            scrollableElementRef.current.scrollTop = newScrollTop;
          }
        });
      }
    };

    const handleDragEnd = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    const handleGlobalMouseUp = handleDragEnd;
    const handleGlobalTouchEnd = handleDragEnd;

    if (isDragging) {
      document.body.style.cursor = 'grabbing';
      globalThis.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
      globalThis.addEventListener('mouseup', handleGlobalMouseUp);
      globalThis.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      globalThis.addEventListener('touchend', handleGlobalTouchEnd);
    } else {
      document.body.style.cursor = '';
    }

    return () => {
      globalThis.removeEventListener('mousemove', handleGlobalMouseMove);
      globalThis.removeEventListener('mouseup', handleGlobalMouseUp);
      globalThis.removeEventListener('touchmove', handleGlobalTouchMove);
      globalThis.removeEventListener('touchend', handleGlobalTouchEnd);
      document.body.style.cursor = '';
    };
  }, [isDragging]);

  // Handle pointer events during drag
  useEffect(() => {
    if (viewerRef.current && fileType === 'pdf') {
      const viewer = viewerRef.current.querySelector('.rpv-core__viewer') as HTMLElement;

      if (isDragging) {
        if (viewer) viewer.style.pointerEvents = 'none';
        viewerRef.current.style.userSelect = 'none';
      } else {
        if (viewer) viewer.style.pointerEvents = 'auto';
        viewerRef.current.style.userSelect = '';
      }
    }
  }, [isDragging, fileType, viewerRef]);

  return {
    isDragging,
    handleMouseDown,
    handleTouchStart,
    handleKeyDown,
    handleDragStart
  };
};